import { useCallback, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { CommonModal } from 'wsm-common-modal';
import { useRequestData } from 'wsm-common-data';
import { usePrefs } from 'ws-scripts/modules/common';

import RatingRow from './RatingRow';
import Pagination from './Pagination';
import ReviewHeader from './ReviewHeader';
import { scrollModalToTop } from '../utilities/modalUtils';
import { ReviewSkeleton, PaginationSkeleton, TotalReviewSkeleton } from './skeleton';
import { SKELETON_REVIEW, KBB_RESEARCHABLE_API } from '../constants';
import { useReviewsFetching } from '../hooks/useReviewsFetching';

import './AllReviewsModal.scss';

const AllReviewsModal = ({ onClose, isOpen }) => {
	// Get vehicleData from Redux store
	const vehicleData = useSelector((state) => state.widgetInfo?.vehicleData);

	const [sortBy, setSortBy] = useState(KBB_RESEARCHABLE_API.sort.MOST_RECENT.label);
	const [filterBy, setFilterBy] = useState(KBB_RESEARCHABLE_API.filter.ALL_RATINGS.label);

	// Pagination state
	const [currentPage, setCurrentPage] = useState(1);

	const { deviceType } = useRequestData();
	const isMobile = deviceType === 'MOBILE';

	const prefs = usePrefs();
	const {
		reviewHeadingWeight = 'font-weight-bold',
		reviewHeadingCase = 'text-capitalize',
		reviewRatingFontSize = 'ddc-font-size-large',
		useInverseTextColor = 'false',
		circleRatingBgColor = 'bg-primary',
		reviewsPerPage = '15' // Pref to control number of reviews per page (US1762682)
	} = prefs;

	const perPage = parseInt(reviewsPerPage, 10) || 15; // Parse pref value to integer, default to 15

	// Use custom hook for reviews fetching
	const {
		paginatedReviews,
		totalPages,
		totalReviews,
		skeletonLoading,
		setTotalReviewsLoading
	} = useReviewsFetching({
		vehicleData,
		isOpen,
		currentPage,
		perPage,
		sortBy,
		filterBy
	});

	const textColor = useInverseTextColor === 'true' ? 'text-inverse' : '';
	const textMutedColor = useInverseTextColor === 'true' ? 'text-muted-inverse' : 'text-muted';

	// Build modal title from vehicle data
	const getModalTitle = useCallback(() => {
		if (vehicleData && vehicleData.year && vehicleData.make && vehicleData.model) {
			return `${vehicleData.year} ${vehicleData.make} ${vehicleData.model}`;
		}
		return 'Vehicle Reviews';
	}, [vehicleData]);

	// Handle page change
	const handlePageChange = useCallback((newPage) => {
		setCurrentPage(newPage);
		scrollModalToTop('.ws-kbb-reviews-modal', 100);
	}, []);

	const renderDropdown = useCallback(({ type, value, options, onChange, disabled = false }) => (
		<div className="d-flex align-items-center pl-4 py-3 border rounded dropdown-wrapper">
			<label className="dropdown-label font-weight-bold mb-0" htmlFor={`dropdown-${type}`}>{type === 'sort' ? 'Sort by:' : 'Rating:'}</label>
			<select
				id={`dropdown-${type}`}
				className={`form-control font-weight-normal h-auto border-0 p-0 pl-2 pointer custom-dropdown ${disabled ? 'inactive' : ''}`}
				value={value}
				onChange={onChange}
			>
				{options.map((option) => (
					<option key={option.id} value={option.label}>
						{option.label}
					</option>
				))}
			</select>
		</div>
	), []);

	const sortOptions = Object.values(KBB_RESEARCHABLE_API.sort);
	const filterOptions = Object.values(KBB_RESEARCHABLE_API.filter);

	const handleSortChange = useCallback((e) => {
		setCurrentPage(1);
		setSortBy(e.target.value);
	}, []);

	const handleFilterChange = useCallback((e) => {
		const filter = e.target.value;

		// Switch sortBy to "Most Recent" when filtering is NOT "All Ratings"
		if (filter !== KBB_RESEARCHABLE_API.filter.ALL_RATINGS.label) {
			setSortBy(KBB_RESEARCHABLE_API.sort.MOST_RECENT.label);
		}
		
		setCurrentPage(1);
		setFilterBy(filter);
		
		setTotalReviewsLoading(true);
	}, [setTotalReviewsLoading]);

	// Use paginated reviews if available, otherwise fall back to initial reviews
	const renderConsumerReview = useCallback((review, index) => {
		const isLastReview = index === paginatedReviews.length - 1;

		return (
			<div
				key={review.id || `review-${index}`}
				className={`d-flex flex-column ${!isLastReview ? 'border-bottom pb-4 mb-4' : ''}`}
			>
				{/* Star rating and title */}
				<ReviewHeader
					review={review}
					reviewHeadingWeight={reviewHeadingWeight}
					reviewHeadingCase={reviewHeadingCase}
					reviewRatingFontSize={reviewRatingFontSize}
					circleRatingBgColor={circleRatingBgColor}
					textColor={textColor}
					textMutedColor={textMutedColor}
				/>

				{/* Review content */}
				{review.pros && (
					<div className="mb-3 pb-3 mr-lg-390 text-break">
						<span className="font-weight-bold text-success mb-1">Pros </span>
						<span>{review.pros}</span>
					</div>
				)}

				{review.cons && (
					<div className="mb-21 mr-lg-390 text-break">
						<span className="font-weight-bold text-danger mb-1">Cons </span>
						<span>{review.cons}</span>
					</div>
				)}

				{review.text && (
					<div className="mb-21 text-break">
						{review.text}
					</div>
				)}
				{/* Individual Review Ratings */}
				<div className="kbb-review-ratings mb-21">
					<div className="row">
						<div className="col-12 col-md-6">
							<RatingRow label="Value" value={review.value} />
							<RatingRow label="Quality" value={review.quality} />
							<RatingRow label="Reliability" value={review.reliability} />
						</div>
						<div className="col-12 col-md-6">
							<RatingRow label="Performance" value={review.performance} />
							<RatingRow label="Comfort" value={review.comfort} />
							<RatingRow label="Styling" value={review.styling} />
						</div>
					</div>
				</div>

				{/* Recommendation indicator */}
				{review.recommendation === 5 && (
					<div className="d-flex align-items-center">
						<span className="text-success mr-2 font-weight-bold">
							✓
						</span>
						<span className="ddc-font-size-small">Recommends this Vehicle </span>
					</div>
				)}
			</div>
		);
	}, [
		reviewHeadingWeight,
		reviewHeadingCase,
		reviewRatingFontSize,
		circleRatingBgColor,
		textColor,
		textMutedColor,
		paginatedReviews.length
	]);

	// Calculate viewed results for display
	const viewedResults = currentPage * perPage;
	const displayViewedResults = Math.min(viewedResults, totalReviews);

	const modalTitle = (
		<div>
			<div className="ddc-font-size-large font-weight-bold">{getModalTitle()}</div>
			<div className="font-weight-normal ddc-font-size-small">Reviews from owners on KBB.com</div>
		</div>
	);

	// Memoize skeleton array for performance
	const skeletonArray = useMemo(() => 
		Array.from({ length: SKELETON_REVIEW }, (_, i) => ({ id: `skeleton-${i}`})), 
	[]);

	// Ensure both dialog elements have proper aria-labelledby
	useEffect(() => {
		if (isOpen) {
			const frame = requestAnimationFrame(() => {
				// Fix outer dialog element that lacks aria-labelledby
				const outerDialog = document.querySelector('div[role="dialog"]:not([aria-labelledby])');
				if (outerDialog) {
					outerDialog.setAttribute('aria-labelledby', 'wsm-common-modal-title');
				}
			});
			return () => cancelAnimationFrame(frame);
		}
		return undefined; // ESLint consistent-return requirement
	}, [isOpen]);
	
	return (
		<CommonModal
			title={modalTitle}
			isOpen={isOpen}
			onClose={onClose}
			width="responsive"
		>
			<div className="ws-kbb-reviews-modal">
				<div className="mb-4 pb-3">
					<div className="d-flex flex-column flex-lg-row justify-content-between">
						<div className="font-weight-bold text-nowrap text-left mb-3 mb-lg-0">
							{skeletonLoading.isLoadingTotalReviews
								? <TotalReviewSkeleton isMobile={isMobile} />
								: `${totalReviews || paginatedReviews.length} Reviews`}
						</div>

						<div className="d-flex flex-row flex-wrap gap-3 mt-3 mt-lg-0">
							{renderDropdown({
								type: 'sort',
								value: sortBy,
								options: sortOptions,
								onChange: handleSortChange,
								disabled: skeletonLoading.isLoadingReviews
							})}
							{renderDropdown({
								type: 'filter',
								value: filterBy,
								options: filterOptions,
								onChange: handleFilterChange,
								disabled: skeletonLoading.isLoadingReviews
							})}
						</div>
					</div>
				</div>

				{/* Display reviews */}
				<div className="reviews-list">
					{skeletonLoading.isLoadingReviews
						? skeletonArray.map((item) => (
							<ReviewSkeleton
								key={item.id}
								isMobile={isMobile}
								isLastReview={item.index === SKELETON_REVIEW - 1}
							/>
						))
						: paginatedReviews?.length > 0 && paginatedReviews.map(renderConsumerReview)}
				</div>

				{/* Pagination section */}
				{skeletonLoading.isLoadingTotalReviews
					? <PaginationSkeleton isMobile={isMobile} />
					: paginatedReviews?.length > 0 && (
						<div className="mt-4 pt-4 border-top">
							{/* "You have viewed X of Y results" text */}
							<div className="mb-3 text-center">
								<span className="text-muted">
									You have viewed {displayViewedResults} of {totalReviews} results
								</span>
							</div>

							{/* Pagination component - show when the page > 1 */}
							{totalPages > 1 && (
								<div className="d-flex justify-content-center">
									<Pagination
										currentPage={currentPage}
										totalPages={totalPages}
										onPageChange={handlePageChange}
										isDisabled={skeletonLoading.isLoadingReviews}
									/>
								</div>
							)}
						</div>
					)
				}

				{/* When calling API failed */}
				{!skeletonLoading.isLoadingReviews && paginatedReviews?.length === 0 && (
					<div className="text-center py-5">
						<p className="text-muted m-0">No reviews available</p>
					</div>)}
			</div>
		</CommonModal>
	);
};

AllReviewsModal.propTypes = {
	onClose: PropTypes.func.isRequired,
	isOpen: PropTypes.bool.isRequired
};

export default AllReviewsModal;